<template>
  <div :class="cls">
    <span v-if="input" :class="`${prefixCls}-input-box`">
      <Input
        v-model="color"
        placeholder="请输入颜色值"
        :disabled="disabled"
        @input="updateValue"
        @change="onInputChange"
      >
        <template #suffix>
          <Trigger
            :disabled="disabled"
            :trigger="['click']"
            position="rt"
            :popup-translate="[10, 0]"
            :click-outside-to-close="true"
            @popup-visible-change="onPopChange"
          >
            <span :class="`${prefixCls}-color`">
              <span :class="`${prefixCls}-alpha`">
                <span
                  :class="`${prefixCls}-value`"
                  :style="{ 'background-color': color }"
                >
                </span>
              </span>
              <span :class="`${prefixCls}-icon`">
                <icon-stop v-if="disabled" />
                <icon-down v-else /> </span
            ></span>
            <template #content>
              <ColorKit
                :theme="theme"
                :color="color"
                @changeColor="changeColor"
              ></ColorKit>
            </template>
          </Trigger> </template
      ></Input>
    </span>
    <trigger
      v-else
      :disabled="disabled"
      :trigger="['click']"
      position="rt"
      :popup-translate="[10, 0]"
      :click-outside-to-close="true"
      @popup-visible-change="onPopChange"
    >
      <template v-if="custom">
        <slot name="default"></slot>
      </template>
      <template v-else>
        <span :class="`${prefixCls}-color`">
          <span :class="`${prefixCls}-alpha`">
            <span
              :class="`${prefixCls}-value`"
              :style="{ 'background-color': color }"
            >
            </span>
          </span>
          <span :class="`${prefixCls}-icon`">
            <icon-stop v-if="disabled" />
            <icon-down v-else /> </span
        ></span>
      </template>
      <template #content>
        <ColorKit
          :theme="theme"
          :color="color"
          @changeColor="changeColor"
        ></ColorKit>
      </template>
    </trigger>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, toRefs } from 'vue';
import { getPrefixCls } from '../_utils/global-config';
import { isNull, isUndefined } from '../_utils/is';
import ColorKit from './color/ColorKit.vue';
import Trigger from '../trigger';
import Input from '../input';
import IconStop from '../icon/icon-stop';
import IconDown from '../icon/icon-down';

export default defineComponent({
  name: 'ColorPicker',
  components: {
    ColorKit,
    Trigger,
    Input,
    IconStop,
    IconDown,
  },
  props: {
    /**
     * @zh 颜色选择器是否可输入颜色
     * @en Whether the color selector can input colors
     */
    input: {
      type: Boolean,
      default: false,
    },
    /**
     * @zh 是否用自定义样式
     * @en Use custom style
     */
    custom: {
      type: Boolean,
      default: false,
    },
    /**
     * @zh 颜色选择器是否可输入颜色
     * @en Whether the color selector can input colors
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * @zh 绑定值
     * @en Value
     */
    modelValue: String,
  },
  emits: {
    'update:modelValue': (value: string) => true,
    /**
     * @zh 用户输入时触发
     * @en Triggered when the user enters
     * @param {string} value
     * @param {Event} ev
     */
    'change': (value: string) => true,
    /**
     * @zh 当颜色的值改变时触发
     * @en Triggered when the value of the color changes
     * @param {string} value
     */
  },
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const color = ref(modelValue.value || '#307af2');
    const changeColor = ({ hex, hsv, rgba }: any) => {
      const { r, g, b, a } = rgba;
      if (a === 1) {
        updateValue(hex);
      } else {
        updateValue(`rgba(${r}, ${g}, ${b}, ${a})`);
      }
    };
    const updateValue = (value: string) => {
      if (isUndefined(value) || isNull(value)) {
        color.value = '';
      }
      color.value = value;
      emit('update:modelValue', value);
      emit('change', value);
    };

    const prefixCls = getPrefixCls('color-picker');
    const cls = computed(() => [
      prefixCls,
      {
        [`${prefixCls}-input`]: props.input,
        [`${prefixCls}-disabled`]: props.disabled,
      },
    ]);

    const theme = ref('dark');
    const onPopChange = () => {
      theme.value =
        document.body.getAttribute('mo-theme') === 'dark' ? 'dark' : 'light';
    };

    const colorHex3To6 = (digit: any) => {
      let _digit = digit;
      _digit = _digit
        .split('')
        .map((item: any) => {
          if (item === '#') return item;
          return item + item;
        })
        .join('');
      if (_digit[0] !== '#') {
        _digit = `#${_digit}`;
      }
      return _digit;
    };

    const onInputChange = (value: string) => {
      if (value.replace('#', '').length === 3)
        color.value = colorHex3To6(value);
    };

    return {
      cls,
      prefixCls,
      changeColor,
      updateValue,
      color,
      theme,
      onPopChange,
      onInputChange,
    };
  },
});
</script>
